// src/pages/MbimRecordsPage.tsx
//
// ★ PHASE 6, TASK 22 — Annex A story 36: "MBIM Record Management" — the ADMIN
// surface over `app.mbim_record` (the entity itself is Task 9's, Annex A 43).
//
// ⚠ MBIM_USER only, both read and write — the one Phase 6 administration
// surface NOT restricted to PSD. See `packages/auth/src/administration-access
// .ts`'s `mayReadMbimRecords` for why.
//
// ⚠ Only `entitlementType = 'OFFICER'` is read by the pro-ration engine
// (`services/proration/src/mbim-validity.ts`, A-31) — `DEPENDANT` is
// catalogued and administrable here but nothing computes against it, because
// PRS2 models no separate dependant identity. Stated on screen so a DEPENDANT
// record established here is never mistaken for one that affects a bill.
import { CodeBadge } from '../components/StatusBadge'
import { plainCode } from '../lib/plain-words'
import { useEffect, useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { api, ApiError} from '../lib/api'
import { compositeSubject, splitCompositeSubject } from '../lib/composite-subject'
import { useApi } from '../lib/useApi'
import { useOffsetPager } from '../lib/useOffsetPager'
import { Pagination } from '../components/Pagination'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { VersionHistory } from '../components/VersionHistory'
import { FormResult } from '../components/FormResult'
import { BoundedField } from '../components/BoundedField'
import { DatePicker } from '../components/DatePicker'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { boundsProblems } from '../lib/published-bounds'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate, formatInstant } from '../lib/format'
import { MBIM_ENTITLEMENT_TYPES, type MbimEntitlementTypeValue } from '../lib/types'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'

type MbimRecordSortKey = 'officerId' | 'entitlementType' | 'issuedOn' | 'mbimNumber'

const COLUMN_LABELS: Readonly<Record<MbimRecordSortKey, string>> = {
  officerId: 'Officer',
  mbimNumber: 'MBIM number',
  entitlementType: 'Entitlement type',
  issuedOn: 'Issued on',
}

/**
 * ★★ WAVE 8 (Task 3) — ONE copy of each bounded field's label: read by
 * `boundsProblems` for its messages AND by the field that renders it. Two copies
 * drift the day one of them is reworded.
 *
 * ⚠ Only fields the SERVER publishes a bound for appear here. A field the route
 * checks for presence alone is deliberately absent, and its screen therefore says
 * nothing beyond presence — see `routes/administration-field-bounds.ts`.
 */
const BOUNDED_FIELDS = {
  mbimNumber: 'MBIM number',
  note: 'Note (optional)',
} as const

/**
 * ★★ WAVE 8 (Task 3) REVIEW FINDING — the bounded field that ONLY EXISTS ON
 * AN ESTABLISH, and is therefore not part of a RETIRE's problem set.
 *
 * ⚠ Ticking the "Retire … instead of superseding it" box unmounts it and drops
 * it from the request body, but the React state lives on this page and survives
 * the unmount. Without naming it here, a value typed before the box was ticked
 * keeps `problems.any` true and **disables Save with no error visible anywhere** —
 * the Wave 5 failure `components/FieldError.tsx`'s header records verbatim. See
 * `lib/published-bounds.ts`'s `BoundsProblemOptions` for the full reasoning.
 *
 * ⚠ This list is the SUBMITTED half of `handleSubmit`'s own `!retireInstead &&`
 * spread below. If one changes, the other has to.
 */
const ESTABLISH_ONLY_FIELDS = ['mbimNumber'] as const

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

/**
 * Rows per page. Comfortably under this surface's own `MAX_*_LIMIT` at the
 * gateway, so the server never has to clamp what the screen asked for — a
 * clamped request would silently show fewer rows than the pager's arithmetic
 * assumes and put the page count out by one.
 */
const PAGE_SIZE = 25

export function MbimRecordsPage() {
  // ★★★ WAVE 12 (P4) — the version history, for a COMPOSITE-KEY catalogue.
  //
  // ⚠ This series needs BOTH keys. Called with only the first, the route answers
  // with the agency's CURRENT BOARD instead of one pair's history — a different
  // question, and one that would render plausibly under a "Version history"
  // heading. So the picker offers a compound subject and `splitCompositeSubject`
  // reads it back; a subject that is not a pair fetches NOTHING rather than
  // guessing.
  const [historySubject, setHistorySubject] = useState('')
  const [historyAsRecordedAt, setHistoryAsRecordedAt] = useState('')
  const history = useApi(() => {
    const pair = splitCompositeSubject(historySubject)
    if (pair === undefined) return Promise.resolve(null)
    return api.mbimRecordSeries(pair.first, pair.second, historyAsRecordedAt)
  }, [historySubject, historyAsRecordedAt])
  const { sort, onSort, interacted } = useSort<MbimRecordSortKey>()
/**
   * ★★ THE PAGE OFFSET — 2026-08-18. This screen showed the first page of a
   * population it also told the officer the size of, and no control could reach
   * the rest; `services/gateway/src/list-reachability.test.ts` is the census
   * that found it. Held here rather than inside `useOffsetPager` because the
   * FETCH is keyed on it, and the hook is deliberately unaware of fetching.
   */
  const [pagerOffset, setPagerOffset] = useState(0)

  const records = useApi(
    () => api.mbimRecords({ ...sortParams(sort), offset: pagerOffset }),
    [sort.column, sort.dir, pagerOffset],
  )

  const [officerId, setOfficerId] = useState('')
  const [entitlementType, setEntitlementType] = useState<MbimEntitlementTypeValue>('OFFICER')
  const [mbimNumber, setMbimNumber] = useState('')
  const [issuedOn, setIssuedOn] = useState('')
  const [note, setNote] = useState('')
  const [retireInstead, setRetireInstead] = useState(false)
  const [form, setForm] = useState<FormState>({ status: 'idle' })

  const rows = records.data?.records ?? []
  const total = records.data?.total ?? 0
  const pager = useOffsetPager(PAGE_SIZE, total)

  // ⚠ The pager owns the page NUMBER; this component owns the offset it FETCHES
  // with. Kept in step in one place rather than becoming two sources of one
  // fact — S-011's shape, in a component.
  useEffect(() => {
    if (pager.offset !== pagerOffset) setPagerOffset(pager.offset)
  }, [pager.offset, pagerOffset])

  // ⚠ A FILTER or SORT change is a different population. Page three of one is
  // not page three of the other, and leaving the offset alone shows an empty
  // table that reads as "no results" when there are plenty.
  useEffect(() => {
    pager.reset()
  }, [sort.column, sort.dir])

  /*
    ⚠ The bounds come from the PAYLOAD (`validation`), never from this file, and
    they are checked against the value that will actually be SENT rather than the
    keystrokes in the box — see `lib/published-bounds.ts` for why both of those
    matter. `undefined` while the list is loading or refused, in which case the
    fields show nothing beyond `required` and the route refuses on its own.
  */
  const validation = records.data?.validation
  const problems = boundsProblems(
    validation,
    {
      mbimNumber: mbimNumber.trim(),
      note: note.trim(),
    },
    BOUNDED_FIELDS,
    // ⚠ A RETIRE sends none of the establish-only fields, so none of them can be
    // in breach — see ESTABLISH_ONLY_FIELDS above for the disabled-with-no-message
    // defect this closes.
    { notSubmitted: retireInstead ? ESTABLISH_ONLY_FIELDS : [] },
  )

  const canSubmit =
    officerId.trim().length > 0 &&
    issuedOn.length > 0 &&
    !problems.any &&
    (retireInstead || mbimNumber.trim().length > 0)

  /**
   * ★★ WAVE 26 — Save FREEZES a sentence and asks; the write happens only from the
   * confirmation (`ConfirmWrite`'s header says why every irreversible write does
   * this now). Composed once, from the fields as they were when Save was pressed —
   * never re-derived from a form the officer may since have edited.
   *
   * ⚠ The sentence says which entitlement type the record is for, because only
   * `OFFICER` records are read by the pro-ration engine (this file's header): a
   * DEPENDANT write must not read as one that changes a bill.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    const officer = officerId.trim()
    const readBy =
      entitlementType === 'OFFICER'
        ? 'the pro-ration engine reads OFFICER records when it validates a bill'
        : 'nothing computes against DEPENDANT records, so no bill changes'
    setConfirming(
      retireInstead
        ? `Retire the ${entitlementType} MBIM record for officer ${officer} from ${issuedOn}. No successor opens, so from that day no MBIM record is current for that officer and entitlement type: ${readBy}. There is no undo. A mistake is corrected by another version.`
        : `Supersede the ${entitlementType} MBIM record for officer ${officer} from ${issuedOn}: MBIM number ${mbimNumber.trim()}. The current record closes on that day and this one is current from then on: ${readBy}. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      const outcome = (await api.supersedeMbimRecord(officerId.trim(), {
        entitlementType,
        issuedOn,
        establish: !retireInstead,
        ...(note.trim().length > 0 && { note: note.trim() }),
        ...(!retireInstead && { mbimNumber: mbimNumber.trim() }),
      })) as { outcome: string }
      setForm({
        status: 'success',
        message:
          outcome.outcome === 'RETIRED'
            ? `${officerId.trim()} (${entitlementType}) retired from ${issuedOn}.`
            : `${officerId.trim()} (${entitlementType}) superseded from ${issuedOn}.`,
      })
      setOfficerId('')
      setMbimNumber('')
      setIssuedOn('')
      setNote('')
      records.reload()
    } catch (error) {
      setForm({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not save this MBIM record.' })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">MBIM records</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            The MBIM record ledger: establish, search and retire the record that the
            maximum validity period expires against. Only records for an officer are used to
            price bills today; records for a dependant can be kept here but do not yet change any bill. A ledger, not an
            edit-in-place table: a renewal is a new record superseding the old one. MBIM Officer only.
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            ★★★ WAVE 27 — one control group, owner-directed. These answer a single
            question — "what do I want to do with this list?" — and were arranged as if
            they answered three or four. See `ExportToolbar` for the whole argument.
          */}
          <ExportToolbar
            onDownload={(format) => {
              window.location.href = api.mbimRecordExportUrl(format)
            }}
            email={<EmailReportButton path="/api/administration/mbim-records/export" />}
          />
        </div>
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={records.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {records.error !== null ? (
        <ErrorCard title="Could not load MBIM records" message={records.error} onRetry={records.reload} />
      ) : records.initialLoading ? (
        <LoadingCard label="Loading MBIM records" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No MBIM records in scope"
          message="Either nothing is currently in force, or your active identity is not the MBIM Officer: this ledger is administered by the MBIM Officer only."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={records.loading}>
            <table className="pt-table">
              <caption className="sr-only">Current MBIM records, with officer, entitlement type, MBIM number and issue date</caption>
              <thead>
                <tr>
                  <SortableTh label="Officer" sortKey="officerId" sort={sort} onSort={onSort} />
                  <SortableTh label="Entitlement type" sortKey="entitlementType" sort={sort} onSort={onSort} />
                  <SortableTh label="MBIM number" sortKey="mbimNumber" sort={sort} onSort={onSort} />
                  <SortableTh label="Issued on" sortKey="issuedOn" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="font-semibold pt-mono">{row.officerId}</td>
                    <td><CodeBadge value={row.entitlementType} /></td>
                    <td className="pt-mono">{row.mbimNumber}</td>
                    <td className="text-sm" title={formatInstant(row.recordedAt)}>
                      {formatIsoDate(row.currency.issuedOn)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            Showing {rows.length} of {total} current record{total === 1 ? '' : 's'}.{' '}
            {sort.column === null ? 'The server default order.' : `Sorted by ${COLUMN_LABELS[sort.column]} (${sort.dir}).`}
          </p>
          <Pagination pager={pager} label="MBIM records" total={total} shown={rows.length} />
        </div>
      )}

      {/* ★★★ WAVE 12 (P4) — the version history. Compound subject, because this
          catalogue's series is keyed by a pair; see the hooks above. */}
      <VersionHistory
        subject={historySubject}
        subjectOptions={rows.map((row) => compositeSubject(row.officerId, row.entitlementType))}
        subjectHint="The officer / entitlement-type pairs listed above. Sort or filter that table to reach others."
        onSubjectChange={(next) => {
          setHistoryAsRecordedAt('')
          setHistorySubject(next)
        }}
        entries={history.data?.entries ?? []}
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
        asRecordedAt={historyAsRecordedAt}
        onAsRecordedAtChange={setHistoryAsRecordedAt}
        // ⚠ An MBIM record is ISSUED and SUPERSEDED, not effective-from/until —
        // `currency: { issuedOn, supersededOn }`. That vocabulary is right for the
        // domain, so the component reads the window through here rather than the
        // catalogue renaming its own fields to suit a shared table.
        windowOf={(entry) => ({ from: entry.currency.issuedOn, until: entry.currency.supersededOn })}
        windowHeaders={{ from: 'Issued on', until: 'Superseded on' }}
        columns={[
            { header: 'Officer', cell: (entry) => entry.officerId },
            { header: 'Entitlement type', cell: (entry) => entry.entitlementType },
        ]}
      />
      <div className="pt-card flex flex-col gap-5 pt-no-print">
        <div>
          <h2 className="text-xl mb-1">Establish or retire a record</h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)]">
            Superseding is the only way a record changes: there is no edit-in-place. The current record for
            this officer and entitlement type (if any) closes on the date you give, and either a new record
            opens with the values below, or (if "retire" is checked) nothing succeeds it.
          </p>
        </div>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          <SgdsInput placeholder="" label="Officer" hintText="e.g. OFF-01" value={officerId} required onSgdsInput={(e) => setOfficerId(targetValue(e))} />
          <SgdsSelect
            label="Entitlement type"
            value={entitlementType}
            onSgdsChange={(e) => setEntitlementType(targetValue(e) as MbimEntitlementTypeValue)}
          >
            {MBIM_ENTITLEMENT_TYPES.map((t) => (
              <SgdsSelectOption key={t} value={t}>
                {plainCode(t)}
              </SgdsSelectOption>
            ))}
          </SgdsSelect>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold">
              Issued on <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </span>
            <DatePicker value={issuedOn} onChange={setIssuedOn} required />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={retireInstead} onChange={(e) => setRetireInstead(e.target.checked)} />
            Retire this record instead of superseding it (no successor opens)
          </label>
          {!retireInstead && (
            <BoundedField
              label={BOUNDED_FIELDS.mbimNumber}
              value={mbimNumber}
              onValueChange={setMbimNumber}
              bounds={validation?.mbimNumber}
              problem={problems.of.mbimNumber}
              required
            />
          )}
          <BoundedField
            className="sm:col-span-2"
            label={BOUNDED_FIELDS.note}
            value={note}
            onValueChange={setNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
          />
          {confirming !== null && (
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          )}
          <FormResult state={form} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <SgdsButton type="submit" variant="primary" disabled={!canSubmit || confirming !== null} loading={form.status === 'submitting'}>
              {retireInstead ? 'Retire' : 'Save'}
            </SgdsButton>
          </div>
        </form>
      </div>
    </div>
  )
}
